import { useEffect } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    AppFeedbackReviewListFilter,
    AppFeedbackRoutingOptionsDto,
    DiscardAppFeedbackDto,
    ReleaseAppFeedbackDto,
    UpdateAppFeedbackReviewSettingsDto,
} from 'breuer-supercharged-sdk';
import { AppFeedbackService } from '@/services/api/app_feedback';
import {
    isForbiddenError,
    reviewPollInterval,
    shouldRetryReviewRequest,
} from '@/pages/feedback-review/feedback_review_utils';

const ROOT = ['app-feedback'] as const;
export const FEEDBACK_ROUTING_OPTIONS_KEY = [...ROOT, 'routing-options'] as const;
export const FEEDBACK_REVIEW_KEY = [...ROOT, 'review'] as const;
const COUNT_KEY = [...FEEDBACK_REVIEW_KEY, 'count'] as const;
const SETTINGS_KEY = [...FEEDBACK_REVIEW_KEY, 'settings'] as const;
const CANDIDATES_KEY = [...FEEDBACK_REVIEW_KEY, 'candidates'] as const;

export const FEEDBACK_REVIEW_POLL_MS = 60_000;

export function useFeedbackRoutingOptions() {
    return useQuery({
        queryKey: FEEDBACK_ROUTING_OPTIONS_KEY,
        queryFn: () => AppFeedbackService.getInstance().getRoutingOptions(),
        staleTime: 30_000,
        retry: 2,
    });
}

/** Bei 403 sind wir kein Prüfer mehr: Rechte neu laden, damit Nav-Eintrag und Abfragen verschwinden. */
function useRefreshRightsOnForbidden(error: unknown) {
    const queryClient = useQueryClient();
    const forbidden = isForbiddenError(error);
    useEffect(() => {
        if (forbidden) void queryClient.invalidateQueries({ queryKey: FEEDBACK_ROUTING_OPTIONS_KEY });
    }, [forbidden, queryClient]);
}

/** Zähler offener Meldungen — nur für Prüfer abfragen (sonst 403); stoppt nach einem 403 sofort. */
export function useFeedbackReviewCount(enabled: boolean) {
    const query = useQuery({
        queryKey: COUNT_KEY,
        queryFn: () => AppFeedbackService.getInstance().countReview(),
        enabled,
        retry: shouldRetryReviewRequest,
        refetchInterval: (q) => reviewPollInterval({ error: q.state.error, active: true, intervalMs: FEEDBACK_REVIEW_POLL_MS }),
        refetchOnWindowFocus: (q) => !isForbiddenError(q.state.error),
    });
    useRefreshRightsOnForbidden(query.error);
    return query;
}

/** `active`: Polling nur, solange die Listenansicht sichtbar ist. */
export function useFeedbackReviewList(
    status: AppFeedbackReviewListFilter,
    page: number,
    pageSize: number,
    active = true,
) {
    const query = useQuery({
        queryKey: [...FEEDBACK_REVIEW_KEY, 'list', status, page, pageSize],
        queryFn: () => AppFeedbackService.getInstance().listReview(status, pageSize, (page - 1) * pageSize),
        placeholderData: keepPreviousData,
        retry: shouldRetryReviewRequest,
        refetchInterval: (q) => reviewPollInterval({ error: q.state.error, active, intervalMs: FEEDBACK_REVIEW_POLL_MS }),
        refetchOnWindowFocus: (q) => !isForbiddenError(q.state.error),
    });
    useRefreshRightsOnForbidden(query.error);
    return query;
}

/** Offene Meldungen werden im Hintergrund nachgeladen, damit eine fremde Entscheidung sichtbar wird. */
export function useFeedbackReviewDetail(id: string | null) {
    const query = useQuery({
        queryKey: [...FEEDBACK_REVIEW_KEY, 'detail', id],
        queryFn: () => AppFeedbackService.getInstance().getReview(id!),
        enabled: !!id,
        retry: shouldRetryReviewRequest,
        refetchInterval: (q) =>
            reviewPollInterval({
                error: q.state.error,
                active: q.state.data?.reviewStatus === 'pending_review',
                intervalMs: FEEDBACK_REVIEW_POLL_MS,
            }),
        refetchOnWindowFocus: (q) => !isForbiddenError(q.state.error),
    });
    useRefreshRightsOnForbidden(query.error);
    return query;
}

export function useInvalidateFeedbackReview() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: FEEDBACK_REVIEW_KEY });
}

export function useReleaseFeedbackReview() {
    return useMutation({
        mutationFn: ({ id, dto }: { id: string; dto: ReleaseAppFeedbackDto }) =>
            AppFeedbackService.getInstance().releaseReview(id, dto),
    });
}

export function useDiscardFeedbackReview() {
    return useMutation({
        mutationFn: ({ id, dto }: { id: string; dto: DiscardAppFeedbackDto }) =>
            AppFeedbackService.getInstance().discardReview(id, dto),
    });
}

export function useFeedbackReviewSettings() {
    return useQuery({
        queryKey: SETTINGS_KEY,
        queryFn: () => AppFeedbackService.getInstance().getReviewSettings(),
        retry: shouldRetryReviewRequest,
    });
}

export function useFeedbackReviewCandidates() {
    return useQuery({
        queryKey: CANDIDATES_KEY,
        queryFn: () => AppFeedbackService.getInstance().listReviewCandidates(),
        retry: shouldRetryReviewRequest,
    });
}

interface UpdateSettingsVariables {
    dto: UpdateAppFeedbackReviewSettingsDto;
    /** Der Nutzer steht danach nicht mehr in der Prüferliste. */
    removesCurrentUser: boolean;
}

export function useUpdateFeedbackReviewSettings() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ dto }: UpdateSettingsVariables) => AppFeedbackService.getInstance().updateReviewSettings(dto),
        onSuccess: async (data, { removesCurrentUser }) => {
            if (removesCurrentUser) {
                // Rechte zuerst auf „kein Prüfer“ setzen, damit kein Count/Listen-Request mehr rausgeht (sonst 403).
                queryClient.setQueryData<AppFeedbackRoutingOptionsDto>(FEEDBACK_ROUTING_OPTIONS_KEY, (old) =>
                    old ? { ...old, currentUserIsReviewer: false } : old,
                );
                await queryClient.cancelQueries({ queryKey: FEEDBACK_REVIEW_KEY });
                queryClient.removeQueries({
                    queryKey: FEEDBACK_REVIEW_KEY,
                    predicate: (q) => q.queryKey[2] === 'count' || q.queryKey[2] === 'list' || q.queryKey[2] === 'detail',
                });
                void queryClient.invalidateQueries({ queryKey: FEEDBACK_ROUTING_OPTIONS_KEY });
                return;
            }
            queryClient.setQueryData(SETTINGS_KEY, data);
            void queryClient.invalidateQueries({ queryKey: FEEDBACK_ROUTING_OPTIONS_KEY });
            void queryClient.invalidateQueries({ queryKey: FEEDBACK_REVIEW_KEY });
        },
    });
}
